import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { HttpClient } from '@angular/common/http'
import { ClientSurfaceEnum, ENV, Environment, currentColorTheme } from 'lib-trend-core'
import { ChatAppTarget, buildChatAppEntryUrl, buildChatAppHandoffUrl } from 'lib-trend-core/chat'

/**
 * Product `/chat` — SPEC-2026-09-internal-chat-pwa (T-42 · RF-19). Where a chat
 * notification lands on a browser registered only through the product: the product
 * session is live here, so this route mints a one-time code and replaces itself with
 * the chat app's handoff landing — the user arrives signed in on the conversation.
 *
 * Accepts `?channel=<id>&message=<id>` — also the legacy shape of the retired in-product
 * chat links, which keep resolving.
 */
@Component({
    selector: 'chat-app-launcher-route',
    template: `
    <div class="d-flex flex-column align-items-center justify-content-center py-5 gap-2">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="text-muted mb-0">{{ 'chat.launcher.opening' | translate }}</p>
    </div>
  `,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChatAppLauncherRouteComponent implements OnInit {

  constructor(
    private readonly route: ActivatedRoute,
    private readonly http: HttpClient,
    @Inject(ENV) private readonly environment: Environment,
  ) { }

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap
    const target: ChatAppTarget = {
      channelId: params.get('channel') || undefined,
      messageId: params.get('message') || undefined,
    }
    const chatAppUrl = String(this.environment['trendWebChatUrl'] ?? '')
    const entryUrl = buildChatAppEntryUrl(chatAppUrl, target)
    if (!entryUrl) {
      window.history.back()
      return
    }

    this.http.post<{ data?: { code?: string } }>('/auth/handoff', { target: ClientSurfaceEnum.WEB_CHAT }).subscribe({
      next: (response) => {
        const code = response?.data?.code
        window.location.replace((code && buildChatAppHandoffUrl(chatAppUrl, target, code, currentColorTheme())) || entryUrl)
      },
      // No code (module off, rate limit): the chat app still opens and asks for sign-in.
      error: () => window.location.replace(entryUrl),
    })
  }
}
